/**
 * The one thing a template-datasource-gated toggle needs to know about the tenant. A structural
 * subset of ITenantTemplateAiSettings (GET Tenant/{id}/TemplateAi), which the tenant panel
 * already loads on every open - so a gated toggle rides that read and costs no extra request.
 */
export interface ITemplateDatasourceGate {
    hasTemplateDatasource: boolean;
}

export interface ITemplateDatasourceToggleState {
    showToggle: boolean;
    toggleDisabled: boolean;
    /** The setting is switched on but the tenant has lost (or never had) a document template datasource. */
    showNoTemplateLibraryWarning: boolean;
}

/**
 * The datasource gate for tenant settings that only matter for documents created from a
 * template datasource. Used directly by "Disable unfilled placeholder warnings", and by
 * templateAiSectionState, which delegates its own datasource branch here so the rule has a
 * single implementation.
 *
 * Three states:
 *
 *  - metadata still loading, the load failed, or the tenant does not exist yet: visible but
 *    disabled, so nothing flickers and nothing is offered before the gate is known;
 *  - no template datasource and the flag off: hidden, because the control would do nothing;
 *  - no template datasource but the flag ON: the escape hatch, a LIVE toggle with a warning.
 *    Hiding here would strand the flag with no way to see or clear it.
 *
 * `enabled` is the DRAFT flag; `wasSavedOn` is the flag as the panel OPENED it, and that is what
 * latches the escape hatch open. Keyed off the draft alone, the toggle would vanish the moment
 * the admin did what the warning asked, taking the only control for the flag with it before
 * Save. The saved flag is fixed for the panel's lifetime, so the latch needs no extra state.
 *
 * Only the datasource gate lives here. templateAiSectionState's other gate, featureAvailable,
 * is about the AI server configuration and must not apply to settings that do not use it.
 */
export const templateDatasourceToggleState = (
    gate: ITemplateDatasourceGate | undefined,
    isUnsavedTenant: boolean,
    enabled: boolean,
    wasSavedOn: boolean): ITemplateDatasourceToggleState => {

    if (gate === undefined || isUnsavedTenant) {
        return { showToggle: true, toggleDisabled: true, showNoTemplateLibraryWarning: false };
    }

    if (!gate.hasTemplateDatasource) {
        return (enabled || wasSavedOn)
            ? { showToggle: true, toggleDisabled: false, showNoTemplateLibraryWarning: true }
            // toggleDisabled is unobservable while hidden; true matches the AI section's HIDDEN
            // constant, which this result is spread over when that helper delegates here.
            : { showToggle: false, toggleDisabled: true, showNoTemplateLibraryWarning: false };
    }

    return { showToggle: true, toggleDisabled: false, showNoTemplateLibraryWarning: false };
};
